import axios from 'axios';
import API_ENDPOINTS from './endpoints';
import { parseApiResponse, extractData, isSuccessResponse } from './responseModel';
import { API_BASE_URL } from './baseUrl';
import {
    delay,
    isNoResponseError,
    NO_RESPONSE_RETRY_COUNT,
    NO_RESPONSE_RETRY_DELAY_MS,
} from './errorUtils';

// Create an Axios instance
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // Increased timeout to 30 seconds
    headers: {
        'Content-Type': 'application/json',
    },
});

// Flag to prevent multiple refresh token calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

// Request Interceptor
axiosInstance.interceptors.request.use(
    (config) => {
        // Get the token from localStorage
        const token = localStorage.getItem('accessToken');
        if (token) {
            // Attach the token to the request headers using Bearer format
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response Interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        // Retry silently when the backend does not respond (e.g. IIS cold start).
        if (isNoResponseError(error)) {
            originalRequest._noResponseRetryCount = originalRequest._noResponseRetryCount || 0;

            if (originalRequest._noResponseRetryCount < NO_RESPONSE_RETRY_COUNT) {
                originalRequest._noResponseRetryCount += 1;
                await delay(NO_RESPONSE_RETRY_DELAY_MS * originalRequest._noResponseRetryCount);
                return axiosInstance(originalRequest);
            }

            error.suppressUserNotification = true;
        }

        // Handle token expiration or unauthorized access
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axiosInstance(originalRequest);
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            // Try to refresh token (no body required, token is in Authorization header)
            try {
                // Attempt to refresh the token (no body)
                const response = await axiosInstance.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN);

                // Parse generic response
                const apiResponse = parseApiResponse(response);
                
                if (isSuccessResponse(apiResponse)) {
                    const data = extractData(apiResponse);
                    
                    if (data && data.accessToken) {
                        const newAccessToken = data.accessToken;
                        const newRefreshToken = data.refreshToken;

                        // Update tokens in localStorage
                        localStorage.setItem('accessToken', newAccessToken);
                        if (newRefreshToken) {
                            localStorage.setItem('refreshToken', newRefreshToken);
                        }

                        // Update the original request with new token
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

                        // Process queued requests
                        processQueue(null, newAccessToken);

                        isRefreshing = false;

                        // Retry the original request
                        return axiosInstance(originalRequest);
                    } else {
                        throw new Error('No token received from refresh endpoint');
                    }
                } else {
                    throw new Error('Token refresh failed');
                }
            } catch (refreshError) {
                // Refresh token failed, logout user
                processQueue(refreshError, null);
                isRefreshing = false;
                
                // Clear all auth data
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                
                // Dispatch logout event or redirect to login
                window.dispatchEvent(new CustomEvent('auth:logout'));
                
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
